import React, { useEffect, useMemo, useState } from 'react';
import { Loader, Message } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';

import { useEmployeeCelebrations } from '../../hooks/useQueries';
import { showToast } from '../../utils/toast';
import './CelebrationsModal.css';

// Everyone's upcoming birthdays and work anniversaries in one date-ordered
// list, grouped by month. Birthdays are only the ones employees chose to share
// in meetings - the server leaves the rest out - and each anniversary carries
// how many years the person will have been with the company on the day.
//
// "Upcoming" is worked out here from the browser's date rather than on the
// server: the people using this are in the office's timezone, and it keeps the
// endpoint a plain list of dates.

const RANGES = [
  { key: '30', label: 'Next 30 days', days: 30 },
  { key: '90', label: 'Next 90 days', days: 90 },
  { key: 'year', label: 'Next 12 months', days: 364 },
];

const KINDS = [
  { key: 'all', label: 'All' },
  { key: 'birthday', label: 'Birthdays' },
  { key: 'anniversary', label: 'Anniversaries' },
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Anniversaries worth calling out as more than another year - the same rule as
// the coaches' weekly anniversary email.
const isMilestone = (years) => years > 0 && years % 5 === 0;

const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// The month/day in a given year, with Feb 29 kept on Feb 28 in a non-leap year
// so those people are not skipped three years in four.
const dateInYear = (year, month, day) => (
  month === 2 && day === 29 && !isLeapYear(year)
    ? new Date(year, 1, 28)
    : new Date(year, month - 1, day)
);

// The first time this month/day falls on or after today.
const nextOccurrence = (today, month, day) => {
  const thisYear = dateInYear(today.getFullYear(), month, day);
  return thisYear >= today ? thisYear : dateInYear(today.getFullYear() + 1, month, day);
};

// Whole days between two local midnights. Rounded, so the hour a DST change
// adds or removes does not knock it off by one.
const daysBetween = (from, to) => Math.round((to - from) / MS_PER_DAY);

const displayName = (emp) => `${emp?.first_name || ''} ${emp?.last_name || ''}`.trim() || emp?.email || 'Unnamed';

const formatYears = (years) => `${years} year${years === 1 ? '' : 's'}`;

const formatDay = (date) => date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const formatMonth = (date) => date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

// "2021-03-03" -> "Mar 3, 2021", read from the string so no timezone moves it.
const formatHireDate = (iso) => {
  const [year, month, day] = String(iso).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatWhen = (daysAway) => {
  if (daysAway === 0) return 'Today';
  if (daysAway === 1) return 'Tomorrow';
  return `in ${daysAway} days`;
};

// Every birthday and anniversary in the next year, soonest first.
const buildEvents = (employees, today) => {
  const events = [];

  for (const emp of employees) {
    const birthday = String(emp.birthday_month_day || '').match(/^(\d{2})-(\d{2})$/);
    if (birthday) {
      const date = nextOccurrence(today, Number(birthday[1]), Number(birthday[2]));
      events.push({
        key: `birthday-${emp.id}`,
        kind: 'birthday',
        date,
        daysAway: daysBetween(today, date),
        employee: emp,
      });
    }

    const hire = String(emp.hire_date || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (hire) {
      const date = nextOccurrence(today, Number(hire[2]), Number(hire[3]));
      const years = date.getFullYear() - Number(hire[1]);
      // Nobody has an anniversary before their first full year.
      if (years >= 1) {
        events.push({
          key: `anniversary-${emp.id}`,
          kind: 'anniversary',
          date,
          daysAway: daysBetween(today, date),
          years,
          employee: emp,
        });
      }
    }
  }

  return events.sort((a, b) => a.date - b.date
    || a.kind.localeCompare(b.kind)
    || displayName(a.employee).localeCompare(displayName(b.employee)));
};

const describeEvent = (event) => (event.kind === 'birthday'
  ? 'Birthday'
  : `${formatYears(event.years)} with the company${isMilestone(event.years) ? ' (milestone)' : ''}`);

// The visible list as plain text, for pasting into a meeting agenda or a
// Teams post.
const buildPlainText = (groups) => groups.map(({ label, events }) => [
  label,
  ...events.map((event) => `  ${formatDay(event.date)} - ${displayName(event.employee)}: ${describeEvent(event)}`),
].join('\n')).join('\n\n');

export default function CelebrationsModal({ open, onClose }) {
  const [rangeKey, setRangeKey] = useState('30');
  const [kind, setKind] = useState('all');

  useEffect(() => {
    if (!open) return;
    setRangeKey('30');
    setKind('all');
  }, [open]);

  const query = useEmployeeCelebrations({ enabled: open });
  const employees = useMemo(() => query.data?.employees || [], [query.data]);
  const birthdaysWithheld = query.data?.birthdaysWithheld || 0;

  // Recomputed whenever the modal opens, so a tab left open overnight does not
  // keep calling yesterday "Today".
  const today = useMemo(() => startOfToday(), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const allEvents = useMemo(() => buildEvents(employees, today), [employees, today]);

  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[0];

  const inRange = useMemo(
    () => allEvents.filter((event) => event.daysAway <= range.days),
    [allEvents, range.days]
  );

  const counts = useMemo(() => ({
    all: inRange.length,
    birthday: inRange.filter((event) => event.kind === 'birthday').length,
    anniversary: inRange.filter((event) => event.kind === 'anniversary').length,
  }), [inRange]);

  const visible = useMemo(
    () => (kind === 'all' ? inRange : inRange.filter((event) => event.kind === kind)),
    [inRange, kind]
  );

  const groups = useMemo(() => {
    const byMonth = [];
    for (const event of visible) {
      const label = formatMonth(event.date);
      const last = byMonth[byMonth.length - 1];
      if (last && last.label === label) last.events.push(event);
      else byMonth.push({ label, events: [event] });
    }
    return byMonth;
  }, [visible]);

  const missingHireDate = employees.filter((emp) => !emp.hire_date).length;

  const copyList = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainText(groups));
      showToast('success', 'List copied to the clipboard.');
    } catch (_e) {
      showToast('error', 'The list could not be copied.');
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="md" overflow>
      <Modal.Header>
        <Modal.Title>Birthdays &amp; work anniversaries</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="celebrations__controls">
          <div className="bdc-segment" role="group" aria-label="Date range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`bdc-btn bdc-btn--sm${rangeKey === r.key ? ' bdc-btn--primary' : ''}`}
                aria-pressed={rangeKey === r.key}
                onClick={() => setRangeKey(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="bdc-segment" role="group" aria-label="Show">
            {KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                className={`bdc-btn bdc-btn--sm${kind === k.key ? ' bdc-btn--primary' : ''}`}
                aria-pressed={kind === k.key}
                onClick={() => setKind(k.key)}
              >
                {k.label}
                <span className="celebrations__count bdc-num">{counts[k.key]}</span>
              </button>
            ))}
          </div>
        </div>

        {query.isLoading ? (
          <Loader content="Loading…" />
        ) : query.isError ? (
          <Message type="error" showIcon>
            {query.error?.message || 'Birthdays and anniversaries could not be loaded.'}
          </Message>
        ) : groups.length === 0 ? (
          <Message type="info" showIcon>
            Nothing to celebrate in the {range.label.toLowerCase()}.
          </Message>
        ) : (
          <div className="celebrations__list">
            {groups.map(({ label, events }) => (
              <section key={label} className="celebrations__month">
                <h3 className="celebrations__month-label">{label}</h3>
                <ul className="celebrations__events">
                  {events.map((event) => {
                    const emp = event.employee;
                    const milestone = event.kind === 'anniversary' && isMilestone(event.years);
                    return (
                      <li
                        key={event.key}
                        className={`celebrations__event${event.daysAway === 0 ? ' is-today' : ''}`}
                      >
                        <div className="celebrations__date">
                          <span className="celebrations__day">{formatDay(event.date)}</span>
                          <span className="celebrations__when">{formatWhen(event.daysAway)}</span>
                        </div>

                        <i
                          className={`bi ${event.kind === 'birthday' ? 'bi-cake2' : 'bi-award'} celebrations__icon celebrations__icon--${event.kind}`}
                          aria-hidden="true"
                        />

                        <div className="celebrations__who">
                          <div className="celebrations__name">{displayName(emp)}</div>
                          <div className="celebrations__meta">
                            {[emp.job_title, emp.coach_name ? `Coach: ${emp.coach_name}` : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </div>

                        <div className="celebrations__what">
                          {event.kind === 'birthday' ? (
                            <span>Birthday</span>
                          ) : (
                            <>
                              <span className="celebrations__years">
                                {formatYears(event.years)}
                                {milestone ? <span className="celebrations__milestone">Milestone</span> : null}
                              </span>
                              <span className="celebrations__joined">
                                Joined {formatHireDate(emp.hire_date)}
                              </span>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}

        {query.isSuccess && (birthdaysWithheld > 0 || missingHireDate > 0) ? (
          <div className="text-muted small celebrations__footnote">
            {birthdaysWithheld > 0
              ? `${birthdaysWithheld} birthday${birthdaysWithheld === 1 ? ' is' : 's are'} on file but not shared in meetings, so ${birthdaysWithheld === 1 ? 'it is' : 'they are'} not listed. `
              : ''}
            {missingHireDate > 0
              ? `${missingHireDate} active employee${missingHireDate === 1 ? ' has' : 's have'} no hire date, so no anniversary can be shown.`
              : ''}
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer
        left={
          <Button icon="clipboard" onClick={copyList} disabled={groups.length === 0}>
            Copy list
          </Button>
        }
      >
        <Button onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
